import { Icon } from "@mdi/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { mdiChevronDown, mdiContentSavePlus, mdiDelete, mdiFileUpload } from "@mdi/js";
import {
  Button,
  Divider,
  Input,
  Popconfirm,
  Select,
  Spin,
  Tooltip,
  UploadProps
} from "antd";
import Dragger from "antd/lib/upload/Dragger";

import { IconSpinner } from "components/icons";

import { Calculation, Field, FieldTemplate, Raster } from "../../models/mapLayer";
import {
  VALID_EXTENSIONS,
  expandDroppedItems,
  expandFileList
} from "../utils/geoMapFileUtils";
import { buildAvailableVariables, hasCircularReference } from "../utils/layerUtils";
import { LabelWrapper } from "./LabelWrapper";
import styles from "./LayerContainer.module.scss";
import { MapRow } from "./MapRow";

interface LayerContainerProps {
  layerName?: string;
  onLayerNameChange?: (name: string) => void;
  onLayerNameBlur?: () => void;
  onFilesSelected?: (files: File[]) => void;
  isUploadingFile: boolean;
  maps?: Array<{ Raster?: Raster; Calculation?: Calculation }>;
  mapTypes?: Field[];
  onDeleteMap?: (map: { Raster?: Raster; Calculation?: Calculation }) => void;
  onMapChange?: (
    map: { Raster?: Raster; Calculation?: Calculation },
    changes: { min?: number; max?: number; unit?: string }
  ) => void;
  onMapNameChanged?: (
    map: { Raster?: Raster; Calculation?: Calculation },
    name: string
  ) => void;
  onExpressionChanged?: (
    map: { Raster?: Raster; Calculation?: Calculation },
    expression: string
  ) => void;
  onUseExpressionChanged?: (
    map: { Raster?: Raster; Calculation?: Calculation },
    checked: boolean
  ) => void;
  calculationFieldTemplates?: FieldTemplate[];
  selectedCalculationMapTemplate?: string;
  onCalculationMapTemplateChange?: (value: string) => void;
  onAddNewCalculationMap?: () => void;
  onSaveMapAsNewTemplate?: () => void;
  onDeleteSelectedTemplate?: () => void;
  isCollapseable?: boolean;
  initialExpanded?: boolean;
  onDeleteLayer?: () => void;
}

export function LayerContainer({
  layerName = "New Zone 1",
  onLayerNameChange,
  onLayerNameBlur,
  onFilesSelected,
  isUploadingFile,
  maps = [],
  mapTypes = [],
  onDeleteMap,
  onMapChange,
  onMapNameChanged,
  onExpressionChanged,
  onUseExpressionChanged,
  calculationFieldTemplates = [],
  selectedCalculationMapTemplate = "Single",
  onCalculationMapTemplateChange,
  onAddNewCalculationMap,
  onSaveMapAsNewTemplate,
  onDeleteSelectedTemplate,
  isCollapseable = false,
  initialExpanded,
  onDeleteLayer
}: LayerContainerProps) {
  // Initialize to true if maps already contains calculation maps
  const [isCalculationMapVisible, setIsCalculationMapVisible] = useState(() =>
    maps.some((m) => m.Calculation)
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragActiveInitial, setIsDragActiveInitial] = useState(false);
  const [isProcessingDrop, setIsProcessingDrop] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Default: expanded if not collapseable, or use explicit initialExpanded if provided
  const [isExpanded, setIsExpanded] = useState(
    initialExpanded !== undefined ? initialExpanded : !isCollapseable
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onFilesSelectedRef = useRef(onFilesSelected);
  useEffect(() => {
    onFilesSelectedRef.current = onFilesSelected;
  });

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Deduplicated list of available variables from raster and calculation maps
  const availableVariables = useMemo(() => buildAvailableVariables(maps), [maps]);

  // IDs of calculation maps that form a circular dependency
  const circularMapIds = useMemo(() => {
    const ids = new Set<string>();
    maps.forEach((m) => {
      if (m.Calculation?.id && hasCircularReference(m.Calculation.name, maps)) {
        ids.add(m.Calculation.id);
      }
    });
    return ids;
  }, [maps]);

  // Process pending files after debounce delay, expanding any .zip files first
  useEffect(() => {
    if (pendingFiles.length > 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const batch = pendingFiles;
        const hasZip = batch.some((f) => f.name.toLowerCase().endsWith(".zip"));
        setPendingFiles([]);

        if (hasZip) {
          setIsProcessingDrop(true);
          expandFileList(batch)
            .then((files) => {
              if (!isMountedRef.current) return;
              if (files.length === 0) {
                toast.error("No valid map files found");
                return;
              }
              toast.info(`${files.length} file${files.length > 1 ? "s" : ""} found`);
              onFilesSelectedRef.current?.(files);
            })
            .catch(() => {
              if (isMountedRef.current) toast.error("Failed to extract archive");
            })
            .finally(() => {
              if (isMountedRef.current) setIsProcessingDrop(false);
            });
        } else {
          // Filter non-zip files to valid extensions before forwarding
          const valid = batch.filter((f) =>
            VALID_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
          );
          onFilesSelectedRef.current?.(valid);
        }
      }, 100); // 100ms debounce
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pendingFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLayerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onLayerNameChange?.(e.target.value);
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Array.from(e.target.files || []);
    // Reset input so same file can be selected again if needed
    e.target.value = "";
    if (raw.length === 0) return;

    setIsProcessingDrop(true);
    try {
      const files = await expandFileList(raw);
      if (!isMountedRef.current) return;
      if (files.length === 0) {
        toast.error("No valid map files found");
        return;
      }
      toast.info(`${files.length} file${files.length > 1 ? "s" : ""} found`);
      onFilesSelectedRef.current?.(files);
    } finally {
      if (isMountedRef.current) setIsProcessingDrop(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const processDroppedItems = async (items: DataTransferItemList) => {
    setIsProcessingDrop(true);
    try {
      const files = await expandDroppedItems(items);
      if (!isMountedRef.current) return;
      if (files.length === 0) {
        toast.error("No valid map files found in the dropped folder or archive");
        return;
      }
      toast.info(`${files.length} file${files.length > 1 ? "s" : ""} found`);
      onFilesSelectedRef.current?.(files);
    } finally {
      if (isMountedRef.current) setIsProcessingDrop(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    processDroppedItems(e.dataTransfer.items);
  };

  const fileDropProps: UploadProps = {
    name: "file",
    multiple: true,
    accept: ".tif,.tiff,.z,.zmap,.prj,.zip",
    showUploadList: false,
    beforeUpload: () => {
      // Prevent automatic upload - we'll handle files manually
      return false;
    },
    onChange(info) {
      const files = info.fileList.map((f) => f.originFileObj).filter(Boolean) as File[];
      setPendingFiles(files);
    }
  };

  const handleAddCalculationMap = () => {
    setIsCalculationMapVisible(true);
  };

  const hasMaps = maps.length > 0;

  const layerContent = (
    <>
      {!hasMaps ? (
        <div
          className={`${styles.draggerWrapper} ${
            isDragActiveInitial ? styles.dragActiveWrapper : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragActiveInitial(true);
          }}
          onDragLeave={() => setIsDragActiveInitial(false)}
          onDropCapture={(e) => {
            const hasDirectory = Array.from(e.dataTransfer.items)
              .filter((i) => i.kind === "file")
              .some((i) => i.webkitGetAsEntry()?.isDirectory);

            if (!hasDirectory) return;

            e.preventDefault();
            e.stopPropagation();
            setIsDragActiveInitial(false);
            processDroppedItems(e.dataTransfer.items);
          }}>
          <Dragger {...fileDropProps}>
            <Icon path={mdiFileUpload} size={2} />
            <p className={styles.uploadText}>
              Drag and drop files, a folder, or a .zip archive here (.tif, .z &amp; .prj,
              .zmap), or click to select files
            </p>
            {(isUploadingFile || isProcessingDrop) && (
              <Spin indicator={<IconSpinner />} />
            )}
          </Dragger>
        </div>
      ) : (
        <div className={styles.mapsScrollContainer}>
          <div
            className={`${styles.mapsContainer} ${isDragActive ? styles.dragActive : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".tif,.tiff,.z,.zmap,.prj,.zip"
              aria-label="Add more map files"
              onChange={handleFileInputChange}
              className={styles.hiddenFileInput}
            />

            <div className={styles.sectionTitleContainer}>
              <div className={styles.sectionTitle}>{layerName} File Map</div>{" "}
              {(isUploadingFile || isProcessingDrop) && (
                <Spin indicator={<IconSpinner />} />
              )}
              <Button
                type="dashed"
                icon={<Icon path={mdiFileUpload} size={"18px"} />}
                onClick={handleAddMoreFiles}
                className={styles.addMoreButton}
                block>
                Add More Files
              </Button>
            </div>

            {maps
              .filter((m) => m.Raster)
              .map((map, index) => (
                <MapRow
                  key={map.Raster?.id ?? index}
                  map={map}
                  mapTypes={mapTypes}
                  onDeleteMap={() => onDeleteMap?.(map)}
                  onChange={async (changes) => {
                    await onMapChange?.(map, changes);
                  }}
                  onMapNameChanged={(name) => {
                    onMapNameChanged?.(map, name);
                  }}
                  onExpressionChanged={async (expression) => {
                    await onExpressionChanged?.(map, expression);
                  }}
                  onUseExpressionChanged={(checked) => {
                    onUseExpressionChanged?.(map, checked);
                  }}
                />
              ))}
          </div>

          {!isCalculationMapVisible ? (
            <Button
              data-testid="add-calculation-map-button"
              type="primary"
              onClick={handleAddCalculationMap}
              className={styles.calculationMapButton}>
              + Add Calculation Map
            </Button>
          ) : (
            <>
              <Divider className={styles.divider} />

              <div className={`${styles.mapsContainer}`}>
                <div className={styles.sectionTitleContainer}>
                  <div className={styles.sectionTitle}>Calculation Map</div>

                  <div className={styles.templateControls}>
                    <LabelWrapper label="Select Template">
                      <Select
                        value={selectedCalculationMapTemplate}
                        onChange={(value) => {
                          onCalculationMapTemplateChange?.(value);
                        }}
                        options={[
                          {
                            label: "Single Calculation",
                            value: "Single"
                          }
                        ].concat(
                          (calculationFieldTemplates ?? []).map((template) => ({
                            label: template.name,
                            value: template.name
                          }))
                        )}
                      />
                    </LabelWrapper>

                    <Button type="primary" onClick={onAddNewCalculationMap}>
                      {selectedCalculationMapTemplate !== "Single"
                        ? "+ Add Template Calcs"
                        : "+ Add Single Calc"}
                    </Button>

                    <Popconfirm
                      title={`Delete template "${selectedCalculationMapTemplate}"?`}
                      onConfirm={() => {
                        onDeleteSelectedTemplate?.();
                      }}
                      okText="Delete"
                      cancelText="Cancel"
                      disabled={selectedCalculationMapTemplate === "Single"}>
                      <Tooltip title="Delete Selected Template">
                        <Button
                          data-testid="delete-template-button"
                          type="text"
                          danger
                          className={styles.templateButton}
                          icon={<Icon path={mdiDelete} size={"18px"} />}
                          disabled={selectedCalculationMapTemplate === "Single"}
                        />
                      </Tooltip>
                    </Popconfirm>

                    {/* Would be good to rework the template userflow here. May be too much to worry about for now. */}
                    <Tooltip title="Save As New Template">
                      <Button
                        data-testid="save-as-template-button"
                        type="text"
                        className={styles.templateButton}
                        icon={<Icon path={mdiContentSavePlus} size={"18px"} />}
                        onClick={onSaveMapAsNewTemplate}
                        disabled={maps.filter((m) => m.Calculation).length === 0}
                      />
                    </Tooltip>
                  </div>
                </div>

                {maps
                  .filter((m) => m.Calculation)
                  .map((map, index) => (
                    <MapRow
                      key={map.Calculation?.id ?? index}
                      map={map}
                      mapTypes={mapTypes}
                      availableVariables={availableVariables}
                      isCircular={circularMapIds.has(map.Calculation?.id ?? "")}
                      onDeleteMap={() => onDeleteMap?.(map)}
                      onChange={async (changes) => {
                        await onMapChange?.(map, changes);
                      }}
                      onMapNameChanged={(name) => {
                        if (name === "") {
                          return;
                        }
                        onMapNameChanged?.(map, name);
                      }}
                      onExpressionChanged={async (expression) => {
                        await onExpressionChanged?.(map, expression);
                      }}
                    />
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={styles.layerContainer}>
      {isCollapseable ? (
        <div className={styles.collapseHeader}>
          <div
            className={`${styles.chevronContainer} ${
              isExpanded ? styles.chevronExpanded : ""
            }`}
            onClick={() => setIsExpanded(!isExpanded)}>
            <Icon path={mdiChevronDown} size="24px" />
          </div>

          <Input
            data-testid="layer-name-input"
            placeholder="Layer Name"
            size="large"
            value={layerName}
            onFocus={(e) => e.target.select()}
            onChange={handleLayerNameChange}
            onBlur={onLayerNameBlur}
            onPressEnter={(e) => e.currentTarget.blur()}
          />

          <Popconfirm
            title="Delete this layer?"
            onConfirm={onDeleteLayer}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}>
            <Button
              type="text"
              className={styles.layerDeleteButton}
              icon={<Icon path={mdiDelete} size="24px" />}
              data-testid="delete-layer-button"
            />
          </Popconfirm>
        </div>
      ) : (
        <Input
          data-testid="layer-name-input"
          placeholder="Layer Name"
          size="large"
          value={layerName}
          onFocus={(e) => e.target.select()}
          onChange={handleLayerNameChange}
          onBlur={onLayerNameBlur}
          onPressEnter={(e) => e.currentTarget.blur()}
        />
      )}

      {(!isCollapseable || isExpanded) && layerContent}
    </div>
  );
}
